import { useCallback, useContext, useMemo } from 'react';
import type { SingleValue } from 'react-select';
import Select from 'react-select';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { ChatCustomFilter, chatCustomFilterNamesMap } from '@trinity/chat';
import { buildSelectStyles, buildSelectTheme } from './Presentation';
import { IsDemoEnvContext } from '@trinity/chat-components/containers/Chat';

type Props = {
  onSelect: (value: ChatCustomFilter) => void;
  value: ChatCustomFilter;
};

type Item = {
  id: ChatCustomFilter;
  name: string;
};

export function CustomFilterTypeSelect({ onSelect, value }: Props) {
  const theme = useTheme();
  const isDemoEnv = useContext(IsDemoEnvContext);

  const selectStyles = useMemo(() => buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => buildSelectTheme(theme), [theme]);

  const items: Item[] = useMemo(() => {
    const filterTypes = [
      isDemoEnv ? null : ChatCustomFilter.CallCenterSource,
      isDemoEnv ? null : ChatCustomFilter.CallCenterSubSource,
      ChatCustomFilter.InteractionType,
      ChatCustomFilter.InteractionSubType,
      isDemoEnv ? null : ChatCustomFilter.InteractionDetail,
      ChatCustomFilter.InteractionDate,
      ChatCustomFilter.InsightType,
      ChatCustomFilter.InterestArea,
      isDemoEnv ? null : ChatCustomFilter.Product,
      ChatCustomFilter.Sentiment,
      ChatCustomFilter.TherapeuticArea,
      ChatCustomFilter.TherapeuticSubArea,
    ].filter(Boolean);
    const items = filterTypes.map(m => ({
      id: m,
      name: chatCustomFilterNamesMap[m],
    }));

    return items;
  }, []);

  const selectedValue = useMemo(() => items.find(f => f.id === value), [items, value]);

  const handleChange = useCallback((value: SingleValue<Item>) => {
    onSelect(value.id);
  }, [onSelect]);

  return (
    <Select<Item>
      styles={selectStyles}
      isSearchable={false}
      theme={selectTheme}
      value={selectedValue}
      options={items}
      placeholder={`Filter Type`}
      getOptionLabel={o => o.name}
      getOptionValue={o => `${o.id}`}
      onChange={handleChange}
      menuPlacement="auto" />
  );
}